import { useState, useCallback, useEffect, useRef } from 'react';
import { favoriteService } from '@/lib/services';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { isCroFlagEnabled } from '@/lib/config/cro-feature-flags';

interface Favorite {
  id: string;
  vehicleId: string;
  car: string;
  location: string;
  price: number;
  rating: number;
  disponible: boolean;
  image_url?: string | null;
  images?: string[] | null;
  make?: string;
  model?: string;
  year?: number;
  is_premium?: boolean;
  reviews_count?: number;
  transmission?: string;
  fuel_type?: string;
  seats?: number;
  status?: string;
}

const GUEST_FAVORITES_KEY = 'rakb_guest_favorites';

const readGuestFavoriteIds = (): string[] => {
  try {
    const raw = localStorage.getItem(GUEST_FAVORITES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeGuestFavoriteIds = (ids: string[]) => {
  try {
    localStorage.setItem(GUEST_FAVORITES_KEY, JSON.stringify(ids));
  } catch { /* quota exceeded — silent */ }
};

const clearGuestFavorites = () => {
  try { localStorage.removeItem(GUEST_FAVORITES_KEY); } catch { /* silent */ }
};

export const useFavorites = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const guestFavoritesFlag = isCroFlagEnabled('CRO_GUEST_FAVORITES');
  const mergedRef = useRef(false);

  const getFavorites = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    try {
      const favoritesData = await favoriteService.getFavorites();

      setFavorites(favoritesData.map((fav: any) => ({
        id: fav.id,
        vehicleId: fav.vehicle_id,
        car: fav.vehicle ? `${fav.vehicle.make || ''} ${fav.vehicle.model || ''} ${fav.vehicle.year || ''}`.trim() : '',
        location: fav.vehicle?.location || '',
        price: fav.vehicle?.price_per_day || 0,
        rating: fav.vehicle?.rating || 0,
        disponible: fav.vehicle?.status === 'available',
        image_url: fav.vehicle?.image_url || null,
        images: fav.vehicle?.images || null,
        make: fav.vehicle?.make || '',
        model: fav.vehicle?.model || '',
        year: fav.vehicle?.year || null,
        is_premium: fav.vehicle?.is_premium || false,
        reviews_count: fav.vehicle?.reviews_count || 0,
        transmission: fav.vehicle?.transmission || null,
        fuel_type: fav.vehicle?.fuel_type || null,
        seats: fav.vehicle?.seats || null,
        status: fav.vehicle?.status || 'unavailable',
      })) as Favorite[]);
    } catch (error) {
      console.error('Error fetching favorites:', error);
      toast.error('Erreur lors du chargement des favoris');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // On login: merge guest localStorage favorites into server
  useEffect(() => {
    if (!user || !guestFavoritesFlag || mergedRef.current) return;
    mergedRef.current = true;
    const guestIds = readGuestFavoriteIds();
    if (guestIds.length === 0) return;
    (async () => {
      try {
        for (const vid of guestIds) {
          const existing = await favoriteService.getFavorite(vid);
          if (!existing) await favoriteService.addFavorite(vid);
        }
        clearGuestFavorites();
        await getFavorites();
      } catch (err) {
        console.error('[useFavorites] merge guest favorites error:', err);
      }
    })();
  }, [user, guestFavoritesFlag, getFavorites]);

  useEffect(() => {
    if (user) {
      getFavorites();
    } else {
      setFavorites([]);
    }
  }, [user, getFavorites]);

  const toggleFavorite = useCallback(async (vehicleId: string) => {
    // Guest favorites: localStorage toggle when logged out + flag on
    if (!user && guestFavoritesFlag) {
      const ids = readGuestFavoriteIds();
      const idx = ids.indexOf(vehicleId);
      if (idx >= 0) {
        ids.splice(idx, 1);
        writeGuestFavoriteIds(ids);
        toast.success('Retiré des favoris');
      } else {
        ids.push(vehicleId);
        writeGuestFavoriteIds(ids);
        toast.success('Ajouté aux favoris');
      }
      return true;
    }

    if (!user) {
      toast.error('Vous devez être connecté pour gérer vos favoris');
      return false;
    }

    try {
      const existing = await favoriteService.getFavorite(vehicleId);

      if (existing) {
        await favoriteService.removeFavorite(vehicleId);
        toast.success('Retiré des favoris');
      } else {
        await favoriteService.addFavorite(vehicleId);
        toast.success('Ajouté aux favoris');
      }

      await getFavorites();
      return true;
    } catch (error) {
      console.error('Erreur lors de la modification des favoris:', error);
      toast.error('Impossible de modifier vos favoris');
      return false;
    }
  }, [user, guestFavoritesFlag, getFavorites]);

  const isFavorite = useCallback((vehicleId: string) => {
    if (!user && guestFavoritesFlag) {
      return readGuestFavoriteIds().includes(vehicleId);
    }
    return favorites.some(f => f.vehicleId === vehicleId);
  }, [favorites, user, guestFavoritesFlag]);

  /** Guest-only IDs (for public /favorites without login) */
  const guestFavoriteIds = guestFavoritesFlag && !user ? readGuestFavoriteIds() : [];

  return {
    favorites,
    loading,
    getFavorites,
    toggleFavorite,
    isFavorite,
    guestFavoriteIds,
  };
};
